import { useEffect, useState } from "react";
import { toast } from "sonner";

// ── Why the event is captured outside React ─────────────────────────────
// `beforeinstallprompt` fires ONCE, very early — typically as soon as the
// manifest and service worker are evaluated, which is usually before React
// has mounted anything at all. A listener attached inside a useEffect (what
// this used to do) therefore misses it on most page loads, and misses it
// ALWAYS on a route the user navigates to later, since the browser never
// re-fires it for the same page. Confirmed live 2026-08-19: the in-app
// install button kept falling back to manual instructions even in Chrome,
// which does support the real prompt — the event just already fired and
// went nowhere before any component-scoped listener existed to catch it.
//
// So it's captured twice over, both outside the component tree, same
// pattern already proven working in the sibling Quran Fluency app
// (src/hooks/usePWAInstall.js there):
//   1. An inline snippet in index.html stashes it on
//      window.__lvDeferredInstallPrompt before the app bundle even
//      downloads.
//   2. The module-level listener below, attached at import time (so it
//      exists before any component does), catches it if the bundle
//      happened to evaluate first.
// Whichever wins, the event ends up in the same module-level slot, and
// every useInstallPrompt() instance subscribes to it — no per-component
// race, because there is no per-component listener anymore.
const GLOBAL_SLOT = "__lvDeferredInstallPrompt";
const GLOBAL_EVENT = "lv:installprompt";

let deferredPrompt = (typeof window !== "undefined" && window[GLOBAL_SLOT]) || null;
const subscribers = new Set();
const notifyAll = () => subscribers.forEach((fn) => fn());

if (typeof window !== "undefined") {
    // Catches the event if this module happened to evaluate before it
    // fired. The inline snippet in index.html covers the more common
    // opposite ordering (fires before any JS bundle has even downloaded).
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredPrompt = e;
        notifyAll();
    });
    // Picks up a capture made by the inline snippet, whichever one won.
    window.addEventListener(GLOBAL_EVENT, () => {
        deferredPrompt = window[GLOBAL_SLOT] || deferredPrompt;
        notifyAll();
    });
    // Covers installing via the browser's OWN address-bar icon/menu, not
    // just our button — a used-up prompt can't be reused either way, so
    // this keeps the module-level slot honest regardless of which UI
    // actually triggered the install.
    window.addEventListener("appinstalled", () => {
        deferredPrompt = null;
        window[GLOBAL_SLOT] = null;
        notifyAll();
    });
}

// Single source of truth for "can/should we offer to install the app right
// now" — both the inline banner (Dashboard/Landing) and the floating popup
// (every other page) read from this one hook, so there is exactly one place
// that knows about beforeinstallprompt, platform detection, install status,
// and dismissal.
//
// v3 (was a plain "dismissed"/"installed" string under _v2): once *any*
// browser/device ever dismissed the banner or completed an install, that
// flag stayed in localStorage forever with no way back — including the
// "installed" case, which never got reconciled against whether the app is
// *currently* still installed. Uninstalling the PWA (or just testing
// install/uninstall a few times, which is exactly what happened here)
// leaves the site permanently believing it's installed, so the offer to
// install never comes back even though isStandalone() correctly says
// otherwise. Confirmed live: an already-installed-then-removed test device
// showed no install banner/popup at all, on any page, indefinitely.
// Storing {reason, ts} instead lets init() reconcile a stale "installed"
// flag against the real current isStandalone() on every load, and lets an
// explicit "dismissed" expire after a few weeks instead of being forever —
// both self-heal on their own from here on, without needing another key
// version bump. The version bump itself also immediately un-sticks anyone
// already stuck under the old plain-string _v2 key, since it's simply not
// read anymore.
const DISMISS_KEY = "lv.pwa.dismissed_v3";
const DISMISS_EXPIRY_MS = 21 * 24 * 60 * 60 * 1000; // re-offer 21 days after an explicit "not now"
// Only ever used for platforms with no native prompt to wait for at all
// (iOS/mac-safari/Firefox) — see the effect below for why "generic"
// (Chrome/Edge/Android) doesn't get a timed fallback of its own.
const FALLBACK_DELAY_MS = 1500;

export function isStandalone() {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function setDismissRecord(reason) {
    try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify({ reason, ts: Date.now() }));
    } catch { /* ignore */ }
}

function readDismissRecord() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

// Reads the stored dismiss record and reconciles it against reality: a
// stale "installed" flag (app no longer running standalone — uninstalled,
// or just being viewed in an ordinary browser tab) or an expired explicit
// dismissal both count as "not dismissed" so the offer comes back.
function readDismissed() {
    const record = readDismissRecord();
    if (!record) return false;
    if (record.reason === "installed") return isStandalone();
    if (record.reason === "dismissed") return Date.now() - record.ts < DISMISS_EXPIRY_MS;
    return true;
}

// Used by AuthContext on sign-out: an explicit "not now" was this
// guardian's own choice, not a permanent site-wide one, so the next
// guardian on this browser (or this one signing back in) should get asked
// again. An "installed" record is left alone — the app really is still
// installed either way, regardless of who's signed in.
export function clearExplicitDismissal() {
    const record = readDismissRecord();
    if (record?.reason === "dismissed") {
        try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
    }
}

export function detectPlatform() {
    const ua = window.navigator.userAgent;
    const isIos = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    if (isIos) return "ios";
    const isMac = /Macintosh/i.test(ua);
    const isSafari = /^((?!chrome|android|crios|edgios).)*safari/i.test(ua);
    if (isMac && isSafari) return "mac-safari";
    if (/Firefox/i.test(ua)) return "firefox";
    return "generic";
}

// "Install desktop app" vs "Install mobile app" wording — Android/iOS get
// the mobile label, everything else (Windows/Mac/Linux desktop browsers)
// gets the desktop label. Purely cosmetic; doesn't affect which platforms
// actually get an install button in the first place.
export function isMobileDevice() {
    return /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}

export function useInstallPrompt() {
    // Mirrors the module-level `deferredPrompt` slot — never sets it
    // directly (see the subscription effect below), so every mounted
    // instance of this hook always agrees on whether a real prompt exists,
    // no matter which one (or which of the two outside-React capture
    // points) actually caught the event.
    const [prompt, setPrompt] = useState(() => deferredPrompt);
    const [platform, setPlatform] = useState(null); // set only once we know there's no native prompt
    const [show, setShow] = useState(false);
    const [installing, setInstalling] = useState(false);
    const [installed, setInstalled] = useState(() => isStandalone());
    const [dismissed, setDismissed] = useState(() => readDismissed());

    useEffect(() => {
        // Fires the moment the OS/browser actually finishes installing —
        // whether triggered by our button or the browser's own UI — so the
        // button flips to "Installed" without needing a reload.
        const onInstalled = () => {
            setInstalled(true);
            setShow(false);
            setDismissRecord("installed");
        };
        window.addEventListener("appinstalled", onInstalled);
        return () => window.removeEventListener("appinstalled", onInstalled);
    }, []);

    // Subscribes to the module-level slot instead of attaching its own
    // beforeinstallprompt listener — see the top-of-file comment for why a
    // per-component listener misses the event on most page loads.
    useEffect(() => {
        if (dismissed || installed) return;

        const sync = () => {
            if (deferredPrompt) {
                setPrompt(deferredPrompt);
                setPlatform(null);
                setShow(true);
            }
        };
        subscribers.add(sync);
        sync(); // catches a capture that landed before this effect ran

        // The manual-instructions fallback is ONLY for browsers that
        // structurally never fire beforeinstallprompt at all — iOS Safari,
        // macOS Safari, Firefox — where there's a genuine, real thing to
        // tell someone (Share -> Add to Home Screen) because no native
        // dialog will ever exist to open instead.
        //
        // Chrome/Edge/Android *do* support the native prompt, so "generic"
        // deliberately does NOT get a timed fallback here — surfacing "look
        // for the install icon in your address bar" there reads as this
        // site being broken/unable to install properly, when the real
        // native "Install app" dialog is exactly what those browsers are
        // supposed to offer on their own timing. Matches the sibling Quran
        // Fluency app's own install UI, which never shows generic hunt-for-
        // the-icon instructions either — nothing renders on those platforms
        // until a real prompt actually arrives.
        const plat = detectPlatform();
        if (plat === "ios" || plat === "mac-safari" || plat === "firefox") {
            const timer = setTimeout(() => {
                if (!deferredPrompt) {
                    setPlatform(plat);
                    setShow(true);
                }
            }, FALLBACK_DELAY_MS);
            return () => {
                subscribers.delete(sync);
                clearTimeout(timer);
            };
        }

        return () => subscribers.delete(sync);
    }, [dismissed, installed]);

    const install = async () => {
        if (!prompt || installing) return;
        setInstalling(true);
        try {
            prompt.prompt();
            const { outcome } = await prompt.userChoice;
            // Single-use whichever way the user answered — Chrome may hand
            // out a fresh one on a later full navigation, but until then
            // there's genuinely nothing left to prompt with.
            deferredPrompt = null;
            if (typeof window !== "undefined") window[GLOBAL_SLOT] = null;
            notifyAll();
            if (outcome === "accepted") {
                // appinstalled also fires and flips `installed`, but do it
                // here too so the UI reacts immediately without waiting on
                // the event loop.
                setShow(false);
                setDismissRecord("installed");
                setDismissed(true);
                toast.success("Little Verses installed!", { description: "You can now launch it from your home screen." });
            }
            // outcome === "dismissed" (user cancelled): no error, just leave
            // the button available so they can try again later.
        } catch {
            deferredPrompt = null;
            notifyAll();
        } finally {
            setInstalling(false);
            setPrompt(null);
        }
    };

    const dismiss = () => {
        setShow(false);
        setDismissRecord("dismissed");
        setDismissed(true);
    };

    return { show, prompt, platform, installing, installed, install, dismiss };
}
